import { Platform } from 'react-native';

// Cross-platform storage utility
class CrossPlatformStorage {
  private isWeb = Platform.OS === 'web';

  async setItem(key: string, value: string): Promise<void> {
    try {
      // Check if the value is too large for storage (5MB limit for localStorage)
      const valueSizeInBytes = new Blob([value]).size;
      const maxSize = 5 * 1024 * 1024; // 5MB limit
      
      if (valueSizeInBytes > maxSize) {
        console.warn(`Storage value too large: ${valueSizeInBytes} bytes. Skipping storage.`);
        return;
      }
      
      if (this.isWeb) {
        // Use localStorage for web
        localStorage.setItem(key, value);
      } else {
        // Use AsyncStorage for mobile
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.setItem(key, value);
      }
    } catch (error) {
      console.warn('Storage setItem failed:', error);
      // If quota exceeded, try to clear old session data
      if (error.name === 'QuotaExceededError' || error.message.includes('QuotaExceededError')) {
        await this.clearOldSessionData();
        // Try again with cleaned storage
        try {
          if (this.isWeb) {
            localStorage.setItem(key, value);
          } else {
            const AsyncStorage = require('@react-native-async-storage/async-storage').default;
            await AsyncStorage.setItem(key, value);
          }
        } catch (retryError) {
          console.warn('Storage setItem retry failed:', retryError);
        }
      }
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      if (this.isWeb) {
        // Use localStorage for web
        return localStorage.getItem(key);
      } else {
        // Use AsyncStorage for mobile
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        return await AsyncStorage.getItem(key);
      }
    } catch (error) {
      console.warn('Storage getItem failed:', error);
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      if (this.isWeb) {
        // Use localStorage for web
        localStorage.removeItem(key);
      } else {
        // Use AsyncStorage for mobile
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.removeItem(key);
      }
    } catch (error) {
      console.warn('Storage removeItem failed:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.isWeb) {
        // Use localStorage for web
        localStorage.clear();
      } else {
        // Use AsyncStorage for mobile
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        await AsyncStorage.clear();
      }
    } catch (error) {
      console.warn('Storage clear failed:', error);
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      if (this.isWeb) {
        // Use localStorage for web
        return Object.keys(localStorage);
      } else {
        // Use AsyncStorage for mobile
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        return await AsyncStorage.getAllKeys();
      }
    } catch (error) {
      console.warn('Storage getAllKeys failed:', error);
      return [];
    }
  }

  async clearOldSessionData(): Promise<void> {
    try {
      const keys = await this.getAllKeys();
      const sessionKeys = keys.filter(key => 
        key.includes('_import_') || 
        key.includes('session_') ||
        key.includes('temp_')
      );
      
      // Remove old session data (older than 1 hour)
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      
      for (const key of sessionKeys) {
        try {
          const data = await this.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.timestamp && parsed.timestamp < oneHourAgo) {
              await this.removeItem(key);
              console.log(`Removed old session data: ${key}`);
            }
          }
        } catch (parseError) {
          // If we can't parse it, remove it
          await this.removeItem(key);
          console.log(`Removed invalid session data: ${key}`);
        }
      }
    } catch (error) {
      console.warn('Failed to clear old session data:', error);
    }
  }
}

// Export singleton instance
export const storage = new CrossPlatformStorage();

// For backward compatibility, also export as default
export default storage;
